import axiosInstance from "@/api/axios-instance";
import { useAuth } from "@clerk/clerk-react";
import { ReactNode, useLayoutEffect, useRef } from "react";

interface ClerkAuthSessionProviderProps {
  children: ReactNode;
}

export default function ClerkAuthSessionProvider({
  children,
}: ClerkAuthSessionProviderProps) {
  const { getToken } = useAuth();
  const interceptorIdRef = useRef<number | null>(null);
  const getTokenRef = useRef(getToken);

  // Keep the ref updated with the latest getToken function
  useLayoutEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  // Use useLayoutEffect to set up interceptor synchronously before browser paint
  // This ensures it's ready before any queries can run
  useLayoutEffect(() => {
    // Remove any existing interceptor first to avoid duplicates
    if (interceptorIdRef.current !== null) {
      axiosInstance.interceptors.request.eject(interceptorIdRef.current);
    }

    // Set up the interceptor immediately
    const interceptor = axiosInstance.interceptors.request.use(
      async (config) => {
        // Use the ref to get the latest getToken function
        // This ensures we always use the current token, even if getToken changes
        const token = await getTokenRef.current();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    interceptorIdRef.current = interceptor;

    return () => {
      if (interceptorIdRef.current !== null) {
        axiosInstance.interceptors.request.eject(interceptorIdRef.current);
        interceptorIdRef.current = null;
      }
    };
  }, []); // Empty deps - set up once, use ref for getToken

  return <>{children}</>;
}
